import { UserRole } from "../constants/roles";
import { PaginatedList, PaginationParams, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";

export enum UserStatus {
  PendingSetup = 0,
  Active = 1,
  Archived = 2,
}

export interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  emailConfirmed: boolean;
  status: UserStatus;
  archivedAt?: string;
  employee?: EmployeeDto;
}

// Comprehensive user response for detailed views (GetById)
export interface UserResponse {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  emailConfirmed: boolean;
  status: UserStatus;
  archivedAt?: string;
  
  // Employee information (if user is an employee)
  isEmployee: boolean;
  employmentStartDate?: string;
  employmentEndDate?: string;
  dateOfBirth?: string;
  familyHoliday?: string;
  worksWeekends: boolean;
  isActive: boolean;
  
  // Address properties
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  
  // Emergency contact properties
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  phoneNumber?: string;

  // Driver-specific information (only for TruckDriver role)
  driverInfo?: EmployeeDriverInfoDto;
  
  // Vehicle assignments (if user is an employee)
  vehicleAssignments: VehicleAssignmentDto[];
  
  // Timestamps
  createdAt: string;
  updatedAt?: string;
}

export interface EmployeeDto {
  id: number;
  userId: number;
  jobCategory: string;
  employmentStartDate: string;
  employmentEndDate?: string;
  isActive: boolean;
  dateOfBirth?: string;
  address: AddressDto;
  emergencyContact: EmergencyContactDto;
  employeeDriverInfo?: EmployeeDriverInfoDto;
}

export interface AddressDto {
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
}

export interface EmergencyContactDto {
  name?: string;
  phone?: string;
}

export interface EmployeeDriverInfoDto {
  employeeId: number;
  companyName?: string;
  ein?: string; // Updated to match API response
  ssn?: string; // Updated to match API response
}

export interface VehicleAssignmentDto {
  id: number;
  vehicleId: number;
  employeeId: number;
  startDate: string;
  endDate?: string;
  vehicleName?: string;
  vehicleVIN?: string;
}

export interface CreateUserDto {
  email: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  isEmployee?: boolean;
  isActive?: boolean;
  employmentStartDate?: string;
  employmentEndDate?: string;
  dateOfBirth?: string;
  familyHoliday?: string;
  worksWeekends?: boolean;
  // Flat properties to match backend DTO
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  phoneNumber?: string;
  driverInfo?: {
    companyName?: string;
    ein?: string;
    ssn?: string;
  };
}

export interface UpdateUserDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: UserRole;
  isEmployee?: boolean;
  isActive?: boolean;
  employmentStartDate?: string;
  employmentEndDate?: string;
  dateOfBirth?: string;
  familyHoliday?: string;
  worksWeekends?: boolean;
  // Flat properties to match backend DTO
  street?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  country?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  phoneNumber?: string;
  driverInfo?: {
    companyName?: string;
    ein?: string;
    ssn?: string;
  };
}

export interface ResendPasswordSetupEmailRequest {
  email: string;
}

export const usersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getUsersPaginated: builder.query<PaginatedList<User>, PaginationParams>({
      query: (params) => ({
        url: "users",
        params: params,
      }),
      providesTags: ["UsersList"],
      transformResponse: unwrapApiResponse,
    }),
    getUsersPaginatedWithSearch: builder.query<
      PaginatedList<User>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize
        };
        
        // Add filters as separate query parameters: filters[key]=value
        if (queryParams.filters && Object.keys(queryParams.filters).length > 0) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              params[`filters[${key}]`] = value;
            }
          });
        }
        
        return {
          url: "users",
          params
        };
      },
      providesTags: ["UsersList"],
      transformResponse: unwrapApiResponse,
    }),
    getUserById: builder.query<UserResponse, number>({
      query: (id) => `users/${id}`,
      providesTags: (result, error, id) => [{ type: "User", id }],
      transformResponse: unwrapApiResponse,
    }),
    createUser: builder.mutation<User, CreateUserDto>({
      query: (user) => ({
        url: "users",
        method: "POST",
        body: user,
      }),
      invalidatesTags: ["UsersList"],
      transformResponse: unwrapApiResponse,
    }),
    updateUser: builder.mutation<User, { id: number; user: UpdateUserDto }>({
      query: ({ id, user }) => ({
        url: `users/${id}`,
        method: "PUT",
        body: user,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        "UsersList", // Invalidate the paginated users list
        "User" // Also invalidate all User queries
      ],
      transformResponse: unwrapApiResponse,
    }),
    deleteUser: builder.mutation<void, number>({
      query: (id) => ({
        url: `users/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["UsersList"],
    }),
    archiveUser: builder.mutation<User, number>({
      query: (id) => ({
        url: `users/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        "UsersList",
      ],
      transformResponse: unwrapApiResponse,
    }),
    unarchiveUser: builder.mutation<User, number>({
      query: (id) => ({
        url: `users/${id}/unarchive`,
        method: "POST",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "User", id },
        "UsersList",
      ],
      transformResponse: unwrapApiResponse,
    }),
    resendPasswordSetupEmail: builder.mutation<boolean, { id: number; email: string }>({
      query: ({ id, email }) => ({
        url: `users/${id}/resend-password-setup`,
        method: "POST",
        body: { email },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "User", id },
        "UsersList",
      ],
    }),
  }),
});

export const {
  useGetUsersPaginatedQuery,
  useGetUsersPaginatedWithSearchQuery,
  useGetUserByIdQuery,
  useLazyGetUserByIdQuery,
  useCreateUserMutation,
  useUpdateUserMutation,
  useDeleteUserMutation,
  useArchiveUserMutation,
  useUnarchiveUserMutation,
  useResendPasswordSetupEmailMutation,
} = usersApi;
